import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import DialogCloseButton from '@product-framework/components/Dialogs/DialogCloseButton';
import ListBoxSelectorStatic, {
  EMPTY_ITEMS,
} from '@product-framework/components/ListBoxSelectorStatic';
import {
  SnackbarApiErrorMessage,
  useDataProvider,
  useLocalization,
  useSnackbar,
} from '@ui-framework/index';
import { useRef, useState } from 'react';

function MultiValueSelector({
  value,
  valueFormatter = () => undefined,
  title,
  getLeftItemsUrl,
  leftNameKey,
  leftValueKey,
  limit,
  leftTitle,
  onChange,
}) {
  const { formatMessage } = useLocalization();
  const [open, setOpen] = useState(false);
  const snackbar = useSnackbar();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { get } = useDataProvider(provider);
  const [displayRightItems, setDisplayRightItems] = useState(
    value || EMPTY_ITEMS,
  );
  const rightItemsRef = useRef(value || EMPTY_ITEMS);

  const isEmptyRight = () => {
    return (
      !Array.isArray(rightItemsRef.current) ||
      (Array.isArray(rightItemsRef.current) &&
        rightItemsRef.current.length === 0) ||
      (Array.isArray(rightItemsRef.current) &&
        rightItemsRef.current[0]?.value === -1)
    );
  };

  const getLeftItems = async page => {
    // If All is selected
    if (
      rightItemsRef.current?.length === 1 &&
      rightItemsRef.current[0].value === ''
    ) {
      return {
        data: [],
        total: rightItemsRef.current.length,
        page: 0,
        limit: 10,
        totalPages: 0,
      };
    }

    const response = await get(
      `${getLeftItemsUrl}?page=${page}&limit=${limit}`,
    );
    if (response.ok) {
      const { data = [], ...other } = response.data;
      const resultsData =
        data?.map(item => ({
          label: item[leftNameKey],
          value: item[leftValueKey],
        })) || [];

      return { data: resultsData, ...other };
    }
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={response?.errors} />,
    });
    return [];
  };

  const getRightItems = pageNumber => {
    if (isEmptyRight()) {
      rightItemsRef.current = [];
    }
    const limitPerPage = 10;
    const totalPages = Math.ceil(rightItemsRef.current.length / limitPerPage);
    const response = {
      data: rightItemsRef.current.slice(
        (pageNumber - 1) * limitPerPage,
        pageNumber * limitPerPage,
      ),
      total: rightItemsRef.current.length,
      page: pageNumber,
      limit: limitPerPage,
      totalPages,
    };
    return response;
  };

  const onItemsAdded = async items => {
    if (items[0].value === '') {
      rightItemsRef.current = items;
    } else {
      rightItemsRef.current = [...rightItemsRef.current, ...items];
    }
    onChange(rightItemsRef.current);
    setDisplayRightItems(rightItemsRef.current);
  };

  const onItemsRemoved = async items => {
    if (items === null || (Array.isArray(items) && items[0]?.value === -1)) {
      rightItemsRef.current = null;
    } else {
      const foundItems = rightItemsRef.current.filter(
        item => !items.some(removed => removed.value === item.value),
      );
      rightItemsRef.current = foundItems?.length > 0 ? foundItems : null;
    }

    onChange(rightItemsRef.current);
    setDisplayRightItems(rightItemsRef.current);
  };

  return (
    <Box>
      <Button sx={{ padding: 0 }} variant="text" onClick={() => setOpen(true)}>
        {valueFormatter ? valueFormatter(displayRightItems) : displayRightItems}
      </Button>
      <Dialog open={open} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {title}
          <DialogCloseButton onClose={() => setOpen(false)} />
        </DialogTitle>

        <DialogContent>
          <ListBoxSelectorStatic
            height="390px"
            getLeftItems={getLeftItems}
            getRightItems={getRightItems}
            parentId={null}
            onItemsAdded={onItemsAdded}
            onItemsRemoved={onItemsRemoved}
            leftTitle={leftTitle}
            rightTitle={formatMessage({
              id: 'RIGHT_HEADING',
              defaultMessage: 'Selected Items',
            })}
            rightInitial={[]}
          />
        </DialogContent>
        <DialogActions
          sx={{
            my: 2,
            gap: 2,
            display: 'flex',
            justifyContent: 'center',
          }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setOpen(false)}>
              {formatMessage({ id: 'CLOSE', defaultMessage: 'Close' })}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default MultiValueSelector;
